import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const KEY = "ecell_cookie_consent";

function getConsent() {
  try {
    return JSON.parse(localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

function applyAnalytics(consent, gaId) {
  if (!consent?.analytics || !gaId || window.__ecellGaLoaded) return;
  window.__ecellGaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", gaId, { anonymize_ip: true });
}

export default function CookieConsent({ gaId }) {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const c = getConsent();
    if (!c) {
      setVisible(true);
    } else {
      applyAnalytics(c, gaId);
    }
  }, [gaId]);

  useEffect(() => {
    const open = () => {
      const c = getConsent();
      setAnalytics(!!c?.analytics);
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", open);
    return () => window.removeEventListener("open-cookie-settings", open);
  }, []);

  const save = (analyticsOn) => {
    const consent = { necessary: true, analytics: analyticsOn, ts: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(consent));
    applyAnalytics(consent, gaId);
    setVisible(false);
    setShowPrefs(false);
  };

  return (
    <>
      {visible && (
        <div
          className="fixed bottom-4 left-4 right-4 md:left-8 md:right-auto md:max-w-md z-[60] animate-in fade-in slide-in-from-bottom-8 duration-500"
          data-testid="cookie-banner"
        >
          <div className="bg-white border border-black/10 shadow-2xl shadow-space/10 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-3">
              <Cookie className="h-5 w-5 text-bell" />
              <h3 className="font-display font-extrabold text-bone tracking-tight text-lg">Cookies</h3>
            </div>
            <p className="text-sm text-bone-muted leading-relaxed">
              We gebruiken noodzakelijke cookies en — enkel met jouw toestemming —
              analytische cookies.
            </p>
            <p className="text-sm text-bone-muted leading-relaxed">
              Lees ons{" "}
              <Link to="/cookiebeleid" className="text-bell hover:underline">cookiebeleid</Link> en{" "}
              <Link to="/privacybeleid" className="text-bell hover:underline">privacybeleid</Link>.
            </p>

            {showPrefs && (
              <div className="mt-4 space-y-3 border-t border-black/5 pt-4">
                <label className="flex items-center justify-between gap-4">
                  <span className="text-sm text-bone">Noodzakelijk <span className="text-bone-faded">(altijd actief)</span></span>
                  <input type="checkbox" checked disabled className="accent-bell h-5 w-5 opacity-60" />
                </label>
                <label className="flex items-center justify-between gap-4 cursor-pointer">
                  <span className="text-sm text-bone">Analytisch (Google Analytics)</span>
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="accent-bell h-5 w-5"
                    data-testid="cookie-toggle-analytics"
                  />
                </label>
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {showPrefs ? (
                <button
                  onClick={() => save(analytics)}
                  className="rounded-full bg-ember text-white px-5 py-2.5 text-sm font-medium hover:bg-ember-hover transition-colors"
                  data-testid="cookie-save-prefs"
                >
                  Voorkeuren opslaan
                </button>
              ) : (
                <>
                  <button
                    onClick={() => save(true)}
                    className="rounded-full bg-ember text-white px-5 py-2.5 text-sm font-medium hover:bg-ember-hover transition-colors"
                    data-testid="cookie-accept-all"
                  >
                    Accepteren
                  </button>
                  <button
                    onClick={() => save(false)}
                    className="rounded-full border border-black/15 text-bone px-5 py-2.5 text-sm font-medium hover:border-bone transition-colors"
                    data-testid="cookie-decline"
                  >
                    Weigeren
                  </button>
                  <button
                    onClick={() => setShowPrefs(true)}
                    className="text-sm text-bone-muted hover:text-bell transition-colors underline underline-offset-4"
                    data-testid="cookie-preferences"
                  >
                    Voorkeuren
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
